import React, { memo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { utilities } from '@weatherbug/common';
import { IconWidget } from '@weatherbug/react';
import { smallMediaQuery } from '../../common/utilities/sizeHelpers';
import { useInterval } from './../../common/hooks/useInterval';
import { forecastRoutes } from  './../../common/translation/translate';
import { CityThumbnail } from './cityThumbnail';
import { CityConditions } from './cityConditions';

const { temperatureUnitAbbreviations } = utilities.units;

const locationType = 'latitudelongitude';
const offset = 0;
const verbose = false;
const refresh = false;
const hourlyInterval = 60 * 60 * 1000;
const dailyInterval = 24 * 60 * 60 * 1000;
const ObsInterval = 5 * 60 * 1000;
const getHourlyForLength = 1; // gets hourly forecast for 1hour, if needed for 5hrs value should be 5 etc.
const roundingProfile = 'DisplayV1';
const cacheDuration = 60 * 1000 * 15;

export const CityCard = memo(({
    city,
    authId,
    secret,
    obsBaseUrl,
    forecastBaseUrl,
    forecastIconResourceList,
    forecastIconSize,
    forecastIconSet,
    contentMgmtBaseUrl,
    stdParameters,
    windSpeedUnit,
    pressureUnit,
    tempUnit,
    lengthUnit,
    getHourlyForecastByLatLon,
    getDailyForecastByLatLon,
    getCurrentStationObsByLatLon
}) => {
    const [hourlyData, setHourlyData] = useState();
    const [iconCode, setIconCode] = useState();
    const [description, setDescription] = useState();
    const [dailyData, setDailyData] = useState();
    const [highTemp, setHighTemp] = useState();
    const [lowTemp, setLowTemp] = useState();
    const [observationData, setObservationData] = useState();
    const [temperature, setTemperature] = useState();

    const href = forecastRoutes.now + city.RouteString;
    const cityLatitude = city?.Latitude;
    const cityLongitude = city?.Longitude;
    const temp = Math.round(temperature);
    const { precipitationUnit, unitSystemsEnum } = utilities.units;
    const units = unitSystemsEnum[tempUnit];
    const tempUnitAbbreviations = temperatureUnitAbbreviations[tempUnit];

    useInterval(
        () => {
            try {
                getHourlyForecastByLatLon({
                    location: `${cityLatitude},${cityLongitude}`,
                    locationType,
                    offset,
                    length: getHourlyForLength,
                    baseUrl: forecastBaseUrl,
                    authId,
                    secret,
                    stdParameters,
                    verbose,
                    refresh,
                    temperatureUnit: tempUnit,
                    pressureUnit,
                    speedUnit: windSpeedUnit,
                    precipitationUnit,
                    roundingProfile,
                    cacheDuration
                }).then(data => {
                    setHourlyData(data?.value?.hf?.hfp?.[0]);
                    setIconCode(data?.value?.hf?.hfp?.[0]?.ic);
                    setDescription(data?.value?.hf?.hfp?.[0]?.d);
                });
            } catch (error) {
                console.log(error);
            }
        },
        hourlyInterval,
        true,
        !!(authId && secret && stdParameters && forecastBaseUrl && cityLatitude && cityLongitude && tempUnit),
        [cityLatitude, cityLongitude, tempUnit]
    );

    useInterval(
        () => {
            try {
                getDailyForecastByLatLon(
                    `${cityLatitude},${cityLongitude}`,
                    locationType,
                    offset,
                    units,
                    forecastBaseUrl,
                    authId,
                    secret,
                    stdParameters,
                    verbose,
                    refresh,
                    tempUnit,
                ).then(data => {
                    setDailyData(data?.value?.dfp[0]);
                    setHighTemp(data?.value?.dfp[0]?.t);
                    setLowTemp(data.value.dfp[1]?.t);
                });
            } catch (error) {
                console.log(error);
            }
        },
        dailyInterval,
        true,
        !!(authId && secret && stdParameters && forecastBaseUrl && cityLatitude && cityLongitude && tempUnit),
        [cityLatitude, cityLongitude, tempUnit]
    );

    useInterval(
        () => {
            try {
                getCurrentStationObsByLatLon(
                    `${cityLatitude},${cityLongitude}`,
                    locationType,
                    offset,
                    units,
                    obsBaseUrl,
                    authId,
                    secret,
                    stdParameters,
                    verbose,
                    refresh,
                    lengthUnit,
                    tempUnit,
                    pressureUnit,
                    windSpeedUnit
                ).then(data => {
                    setObservationData(data?.value);
                    setTemperature(data?.value?.o?.t);
                });
            } catch (error) {
                console.log(error);
            }
        },
        ObsInterval,
        true,
        !!(authId && secret && stdParameters && obsBaseUrl && cityLatitude && cityLongitude && tempUnit),
        [cityLatitude, cityLongitude, tempUnit]
    );

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    if (!hourlyData || !dailyData || !observationData) {
        return null;
    }
    return (
        <Card>
            <Anchor href={href}>
                <CityThumbnail
                    cityName={city.CityName}
                    cityLabel={city.Label}
                />
                <Info>
                    <IconWrapper>
                        <IconWidget
                            iconCode={iconCode}
                            iconSet={forecastIconSet}
                            iconSize={forecastIconSize}
                            authId={authId}
                            secret={secret}
                            contentManagementBaseUrl={contentMgmtBaseUrl}
                            stdParameters={stdParameters}
                            resourceList={forecastIconResourceList}
                        />
                    </IconWrapper>
                    <CityConditions
                        temperature={temp}
                        highTemperature={highTemp}
                        lowTemperature={lowTemp}
                        description={description}
                        tempUnitAbbreviations={tempUnitAbbreviations}
                    />
                </Info>
            </Anchor>
        </Card>
    );
});

CityCard.displayName = 'CityCard';
CityCard.propTypes = {
    city: PropTypes.object,
    authId: PropTypes.string,
    secret: PropTypes.string,
    obsBaseUrl: PropTypes.string,
    forecastBaseUrl: PropTypes.string,
    stdParameters: PropTypes.object,
    windSpeedUnit: PropTypes.number,
    pressureUnit: PropTypes.number,
    tempUnit: PropTypes.number,
    lengthUnit: PropTypes.number,
    getHourlyForecastByLatLon: PropTypes.func,
    getDailyForecastByLatLon: PropTypes.func,
    getCurrentStationObsByLatLon: PropTypes.func,
    forecastIconResourceList: PropTypes.string,
    forecastIconSize: PropTypes.string,
    forecastIconSet: PropTypes.string,
    contentMgmtBaseUrl: PropTypes.string,
    user: PropTypes.object
};

const Card = styled.li`
    padding: 5px;
    width: 50%;
    display: inline-block;
    vertical-align: top;

    @media ${smallMediaQuery} {
        width: 100%;
        margin: 6px 0 0 0;
    }
`;

const Anchor = styled.a`
    display: block;
    text-decoration: none;
`;

const Info = styled.div`
    padding: 10px;
    font-size: 0;
    background-color: white;
    border-radius: 0 0 2px 2px;
    display: flex;
    flex-direction: row;
`;

const IconWrapper = styled.div`
    & .wb-content-mgt-icon-image {
            width: 60px;
            height: 60px;
    }
`;
